import { approach, clamp01, easeInOutCubic, easeInQuad, easeOutCubic, lerp, pneu, seg } from './easing'
import { cameraAt, cameraVars, register } from './timelines'

/**
 * The assembly film as a pure function of its progress p (0..1).
 * A parallel gripper on the carriage picks the printed ISOFIX cap from the
 * pad film's fixture, carries it over the housing and aligns it on one axis,
 * then presses it in: the snap hooks ride the lead-in chamfer (deflecting
 * inward), click out into the undercut windows and the cap drops to its
 * seat. The nest shuttles the assembled part to a spring-loaded probe, the
 * check prints, the part goes into register.
 *
 * Heights are stage offsets from the cap sitting in the pad fixture: seated
 * in the housing it is 22 higher (the housing top is h 80, the nest h 58).
 *
 * It loops on a match cut: the film ends tight on the mark of the assembled
 * cap and the next loop opens tight on the mark of the next printed cap,
 * waiting in the supply fixture, then pulls back as the gripper comes down.
 */

const ASSEMBLY_GEOMETRY = { supply: 484, asm: 1124, up: -150, seat: -22, nest: 150 }
const { supply: SUPPLY, asm: ASM, up: UP, seat: SEAT, nest: NEST } = ASSEMBLY_GEOMETRY
/* The barb meets the chamfer 20 above the seat, has ridden it at 15 (hooks
   fully in), slides down the wall and catches the window at the seat. */
const CONTACT = SEAT - 20
const RIDDEN = SEAT - 15
const SLID = SEAT - 1.5
const HOOK_MAX = 12
/* Probe: its pin lands on the cap face beside the mark (never on the fresh
   print) at 102; the spring then takes 11.4. */
const TOUCH = 102.03
const DEEP = TOUCH + 11.4
const SPRING = 30

export const ASSEMBLY_STATIONS = [0, 0.36, 0.62]
export const ASSEMBLY_BEATS = [0.1, 0.5, 0.74]
const RELEASE = 0.53

function carriage(p) {
  if (p < 0.18) return SUPPLY
  if (p < 0.32) return lerp(SUPPLY, ASM, pneu(seg(p, 0.18, 0.32), 0.008))
  if (p < 0.6) return ASM
  if (p < 0.7) return lerp(ASM, SUPPLY, pneu(seg(p, 0.6, 0.7), 0.008))
  return SUPPLY
}

/** Press-in: decelerate into the chamfer, ride it, slide, click into the seat. */
function insert(p) {
  if (p < 0.44) return lerp(UP, CONTACT, easeOutCubic(seg(p, 0.36, 0.44)))
  if (p < 0.47) return lerp(CONTACT, RIDDEN, easeInQuad(seg(p, 0.44, 0.47)))
  if (p < 0.5) return lerp(RIDDEN, SLID, seg(p, 0.47, 0.5))
  return lerp(SLID, SEAT, easeOutCubic(seg(p, 0.5, 0.505)))
}

/** Gripper height (stage dy; 0 = gripping the cap in the supply fixture). */
function gripDy(p) {
  if (p < 0.04) return UP
  if (p < 0.1) return UP * (1 - pneu(seg(p, 0.04, 0.1), 0.01))
  if (p < 0.13) return 0
  if (p < 0.18) return UP * pneu(seg(p, 0.13, 0.18), 0.01)
  if (p < 0.36) return UP
  if (p < 0.505) return insert(p)
  if (p < 0.56) return SEAT
  if (p < 0.6) return lerp(SEAT, UP, pneu(seg(p, 0.56, 0.6), 0.01))
  return UP
}

/** Finger closure, 0 open .. 1 closed on the cap walls. */
function grip(p) {
  if (p < 0.1) return 0
  if (p < 0.13) return Math.min(1, pneu(seg(p, 0.1, 0.13), 0))
  if (p < RELEASE) return 1
  if (p < 0.56) return 1 - Math.min(1, pneu(seg(p, RELEASE, 0.56), 0))
  return 0
}

function nestX(p) {
  if (p < 0.62) return 0
  if (p < 0.7) return NEST * pneu(seg(p, 0.62, 0.7), 0.01)
  return NEST
}

/** Snap hooks, degrees inward: ride the chamfer, slide, snap out past 0, settle. */
function hook(p) {
  if (p < 0.44 || p >= 0.53) return 0
  if (p < 0.47) return HOOK_MAX * easeInQuad(seg(p, 0.44, 0.47))
  if (p < 0.5) return HOOK_MAX
  if (p < 0.505) return lerp(HOOK_MAX, -3, easeOutCubic(seg(p, 0.5, 0.505)))
  return lerp(-3, 0, easeInOutCubic(seg(p, 0.505, 0.53)))
}

/** Probe holder travel: approach at speed, compress the spring, dwell, lift. */
function probe(p) {
  if (p < 0.7 || p >= 0.82) return 0
  if (p < 0.73) return TOUCH * approach(seg(p, 0.7, 0.73))
  if (p < 0.76) return lerp(TOUCH, DEEP, easeOutCubic(seg(p, 0.73, 0.76)))
  if (p < 0.78) return DEEP
  return DEEP * (1 - pneu(seg(p, 0.78, 0.82), 0.01))
}

const WIDE = { s: 1, x: 800, y: 455 }
const AT_SUPPLY = { s: 1.4, x: 560, y: 500 }
const AT_HOUSING = { s: 1.8, x: 1124, y: 540 }
/* The click is read in its detail bubble, left of the housing. */
const AT_CLICK = { s: 2, x: 1040, y: 566 }
const MEDIUM = { s: 1.3, x: 900, y: 480 }
const AT_PROBE = { s: 1.6, x: 1330, y: 492 }
const AT_PROBE_CLOSE = { s: 1.65, x: 1350, y: 488 }
/* The match cut: the cap's mark in the supply fixture (face 484, 550) and
   seated in the nest under the probe (face 1274, 528), framed alike. */
const CUT_S = 3
const SUPPLY_CLOSE = { s: CUT_S, x: SUPPLY, y: 545 }
const NEST_CLOSE = { s: CUT_S, x: ASM + NEST, y: 545 + SEAT }
const CAMERA = [
  [0, 0.05, SUPPLY_CLOSE, AT_SUPPLY],
  [0.18, 0.36, AT_SUPPLY, AT_HOUSING],
  [0.37, 0.42, AT_HOUSING, AT_CLICK],
  [0.57, 0.66, AT_CLICK, MEDIUM],
  [0.67, 0.71, MEDIUM, AT_PROBE],
  [0.84, 0.9, AT_PROBE, AT_PROBE_CLOSE],
  [0.94, 0.995, AT_PROBE_CLOSE, NEST_CLOSE],
]

/** The click's detail bubble: open as the barb nears the chamfer, closed once the fingers let go. */
function clickDetail(p) {
  if (p < 0.38) return 0
  if (p < 0.56) return easeOutCubic(seg(p, 0.38, 0.41))
  return 1 - easeInQuad(seg(p, 0.56, 0.59))
}

/** @param {number} p film progress 0..1 */
export function assemblyFrame(p) {
  const cam = cameraAt(p, CAMERA)
  const xs = carriage(p)
  const dy = gripDy(p)
  const carried = p >= 0.13 && p < RELEASE
  const nest = nestX(p)
  const holder = probe(p)
  return {
    vars: {
      ...cameraVars(cam),
      'pad-x': xs,
      'grip-dy': dy,
      grip: grip(p),
      'cap-x': p < 0.13 ? SUPPLY : carried ? xs : ASM + nest,
      'cap-dy': p < 0.13 ? 0 : carried ? dy : SEAT,
      hook: hook(p),
      axis: easeOutCubic(seg(p, 0.3, 0.36)) + seg(p, 0.58, 0.62),
      click: p < 0.5 ? 0 : p < RELEASE ? seg(p, 0.5, 0.51) : 1 - seg(p, RELEASE, 0.56),
      'nest-x': nest,
      'probe-dy': holder,
      spring: 1 - clamp01((holder - TOUCH) / SPRING),
      check: seg(p, 0.8, 0.83),
      detail: clickDetail(p),
      ...register(p - 0.08),
    },
    station: p < ASSEMBLY_STATIONS[1] ? 0 : p < ASSEMBLY_STATIONS[2] ? 1 : 2,
  }
}

/** The still: the assembled part checked and in register, the whole cell in shot. */
export function assemblyPoster() {
  const frame = assemblyFrame(0.93)
  return { ...frame, vars: { ...frame.vars, ...cameraVars(WIDE) } }
}
